import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    ArrowDownCircle,
    ArrowUpCircle,
    MinusCircle,
    TrendingDown,
    TrendingUp,
    Warehouse,
} from 'lucide-react';
import { useGetList } from 'ra-core';
import { Link } from 'react-router-dom';

const TxRow = ({ tx }: { tx: any }) => {
    const isIn = tx.transaction_type === 'IN';
    const isAdjust = tx.transaction_type === 'ADJUST';
    const Icon = isAdjust ? MinusCircle : isIn ? ArrowDownCircle : ArrowUpCircle;
    const colorClass = isAdjust
        ? 'text-muted-foreground'
        : isIn
        ? 'text-emerald-600'
        : 'text-red-500';

    return (
        <div className="flex items-center gap-3 py-2.5 border-b last:border-none group hover:bg-muted px-1 rounded transition-colors">
            <Icon className={`w-4 h-4 shrink-0 ${colorClass}`} />
            <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">{tx.notes || tx.reference_type || 'Stock Movement'}</p>
                <p className="text-[10px] text-muted-foreground">
                    {new Date(tx.transaction_date || tx.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                    })}
                    {tx.reference_type && <> &middot; {tx.reference_type}</>}
                </p>
            </div>
            <div className={`text-sm font-black tabular-nums ${colorClass}`}>
                {isIn ? '+' : isAdjust ? '±' : '-'}{Math.abs(tx.quantity)}
            </div>
            <Badge
                variant="outline"
                className={`text-[9px] font-bold shrink-0 ${
                    isIn ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : isAdjust ? 'bg-muted text-muted-foreground border-border'
                    : 'bg-red-50 text-red-600 border-red-200'
                }`}
            >
                {tx.transaction_type}
            </Badge>
        </div>
    );
};

/**
 * StockMovementWidget
 *
 * Dashboard card showing the last 15 inventory transactions across all items.
 * IN (GRN inbound) shown in green, OUT (DC outbound) in red, ADJUST in slate.
 */
export const StockMovementWidget = () => {
    const { data: txns, isPending } = useGetList('inventory_transactions', {
        pagination: { page: 1, perPage: 15 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: {},
    });

    const inCount = txns?.filter((t) => t.transaction_type === 'IN').length ?? 0;
    const outCount = txns?.filter((t) => t.transaction_type === 'OUT').length ?? 0;

    return (
        <Card className="border-none shadow-sm">
            <CardHeader className="pb-3 border-b">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Warehouse className="w-3.5 h-3.5" /> Stock Movements
                    <div className="ml-auto flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> {inCount} IN
                        </Badge>
                        <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200 flex items-center gap-1">
                            <TrendingDown className="w-3 h-3" /> {outCount} OUT
                        </Badge>
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-2 pb-0">
                {isPending ? (
                    <div className="py-8 text-center text-slate-300 text-xs animate-pulse">Loading movements…</div>
                ) : !txns?.length ? (
                    <div className="py-8 text-center text-slate-300 flex flex-col items-center gap-2">
                        <Warehouse className="w-8 h-8" />
                        <p className="text-xs">No stock movements yet.</p>
                    </div>
                ) : (
                    <div>
                        {txns.map((tx) => <TxRow key={tx.id} tx={tx} />)}
                    </div>
                )}
                <div className="pt-2 pb-3 text-center">
                    <Link to="/inventory_items" className="text-[11px] text-blue-600 hover:underline font-semibold">
                        View full inventory →
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
};
